import Route from '@/generated/classes/Route';
import { CiRouter } from '@/framework/base/helpers/NavHelper';

// Ouvrir une section en modale depuis le client et attendre qu'elle soit refermée.
//
// Les routes renvoyées par le serveur (`HttpRoute`) savent reprendre un traitement à la fermeture :
// `RouteManager.ShowRoute` mémorise un callback, que `Route.closeSection` déclenche. Une route ouverte
// par le client avec `CiRouter.addRoute` n'a rien de tel — App.vue la retire de `listRoutes` sans
// prévenir personne. Ce module comble ce manque, pour qu'une action de masse puisse réutiliser les
// modales existantes (identité de l'établissement, signataire…) au lieu d'en recoder.
//
// La promesse est résolue à la fermeture, que l'utilisateur ait enregistré ou annulé : l'appelant
// relit ses données pour savoir où il en est. C'est ce qui évite de toucher aux sections réutilisées.

interface Attente {
  route: Route;
  resoudre: () => void;
}

const attentes: Attente[] = [];

/**
 * Égalité de routes au sens de la pile de modales d'App.vue, qui s'en sert pour dédoublonner à
 * l'ouverture et retrouver la route à fermer. `ficheObjId` n'y entre pas.
 */
export function memeRoute(r1: Route, r2: Route): boolean {
  return r1.module == r2.module &&
    r1.fiche == r2.fiche &&
    r1.section == r2.section &&
    r1.sectionObjId == r2.sectionObjId &&
    (((r1.attribut as { name?: unknown } | null)?.name) == ((r2.attribut as { name?: unknown } | null)?.name));
}

/**
 * Ouvre `route` et résout à sa fermeture.
 *
 * ⚠️ `route.isDialog` doit valoir `true` : sans quoi NotifSections navigue vers le module au lieu
 * d'ouvrir une modale, et la referme aussitôt — la promesse serait résolue sans que rien n'ait été
 * montré.
 *
 * Si une route équivalente est déjà ouverte, App.vue n'en ajoute pas de seconde : la promesse est
 * alors résolue à la fermeture de celle qui est affichée, ce qui reste le bon moment pour relire.
 */
export function ouvrirEtAttendre(route: Route): Promise<void> {
  return new Promise((resolve, reject) => {
    const attente: Attente = { route, resoudre: resolve };
    attentes.push(attente);
    try {
      CiRouter.addRoute(route);
    } catch (err) {
      // `addRouteSetter` est posé par App.vue au démarrage : son absence ne doit pas laisser une
      // attente orpheline, qu'aucune fermeture ne viendrait jamais résoudre.
      attentes.splice(attentes.indexOf(attente), 1);
      reject(err);
    }
  });
}

/** Appelée par App.vue à chaque fermeture de modale : résout les attentes portant sur cette route. */
export function notifierFermeture(route: Route): void {
  for (let i = attentes.length - 1; i >= 0; i--) {
    if (memeRoute(attentes[i].route, route)) {
      const [attente] = attentes.splice(i, 1);
      attente.resoudre();
    }
  }
}
